import { fetchGet, fetchPost } from "./client";
import {
  Orientation,
  Center,
  Bounds,
  isCoordinates,
  Scale,
  PageSize,
  S3Asset,
} from "../types/OsMapPrint";

const URL_ROOT = `/map-printing/os-mm`;

export type FetchBoundsOptions = {
  sku_id: string;
  orientation: Orientation;
  center: Center;
};

type FetchBoundsOutput = {
  abort: AbortController["abort"];
  bounds: { bounding_box_polygon: Bounds };
};

export const fetchBounds = async ({
  sku_id,
  orientation,
  center,
}: FetchBoundsOptions): Promise<FetchBoundsOutput | undefined> => {
  const url = `${URL_ROOT}/sku/${sku_id}/bounding-box-polygon`;

  const centerCoords = isCoordinates(center) ? center : center.coordinates;

  try {
    const { abort, result } = fetchGet(url, {
      orientation,
      center: centerCoords.join(","),
    });
    const bounds = await (await result).json();

    return {
      abort,
      bounds,
    } as FetchBoundsOutput;
  } catch (_) {
    return;
  }
};

type PreviewPayload = {
  site_id: string | null;
  orientation: Orientation;
  center: Center;
};

type PreviewOutput = {
  abort: AbortController["abort"];
  image: Blob;
};

export const preview = async (sku_id: string, payload: PreviewPayload) => {
  const url = `${URL_ROOT}/sku/${sku_id}/preview`;

  try {
    const { abort, result } = fetchPost(url, payload);
    const image = await (await result).blob();

    return {
      abort,
      image,
    } as PreviewOutput;
  } catch (_) {
    return;
  }
};

export type PurchasePayload = {
  token?: string;
  card_id?: string;
  site_id: string | null;
  orientation: Orientation;
  center: Center;
};

type PurchaseResponse = {
  orientation: Orientation;
  center: Center;
  bounding_box_polygon: Bounds;
  scale: Scale;
  page_size: PageSize;
  sku_id: string;
  s3_assets: S3Asset[];
  _id: string;
};

type PurchaseOutput = {
  abort: AbortController["abort"];
  response: PurchaseResponse;
};

export const purchase = async (sku_id: string, payload: PurchasePayload) => {
  const url = `${URL_ROOT}/sku/${sku_id}/purchase`;

  const { abort, result } = fetchPost(url, payload);
  const response = await result;

  if (!response.ok) {
    throw new Error("" + response.status);
  }

  const responseJson: PurchaseResponse = await response.json();

  return {
    abort,
    response: responseJson,
  } as PurchaseOutput;
};
